import axios from "axios";
import {
    TOKEN_STORAGE_KEY as TOKEN_KEY,
    REFRESH_TOKEN_STORAGE_KEY as REFRESH_TOKEN_KEY,
    AUTH_METHOD_STORAGE_KEY as AUTH_METHOD_KEY,
    TENANT_STORAGE_KEY,
} from "@/auth/authStorageKeys";

const tokenRef = { current: null as string | null };

export const setToken = (token: string | null) => {
    tokenRef.current = token;
};

export const setRefreshToken = (refreshToken: string | null) => {
    if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } else {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
};

const baseURL = import.meta.env.DEV ? "/api" : import.meta.env.VITE_API_BASE_URL;

const clearSession = () => {
    setToken(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_METHOD_KEY);
    localStorage.removeItem(TENANT_STORAGE_KEY);
};

// Local login and the Azure token-exchange endpoint both end up issuing the same kind of
// Hockeyly token + refresh token pair, so a single refresh path covers both auth methods.
// Concurrent 401s share one in-flight refresh call instead of each triggering their own.
let refreshPromise: Promise<string | null> | null = null;

const refreshAccessToken = (): Promise<string | null> => {
    if (!refreshPromise) {
        refreshPromise = (async () => {
            const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
            if (!storedRefreshToken) {
                return null;
            }

            try {
                const response = await axios.post<{ token: string; refreshToken: string }>(`${baseURL}/refresh-token`, {
                    refreshToken: storedRefreshToken,
                });
                setToken(response.data.token);
                localStorage.setItem(TOKEN_KEY, response.data.token);
                setRefreshToken(response.data.refreshToken);
                return response.data.token;
            } catch {
                return null;
            }
        })().finally(() => {
            refreshPromise = null;
        });
    }

    return refreshPromise;
};

const axiosClient = () => {
    const defaultOptions = {
        baseURL,
        headers: {
            "Content-Type": "application/json",
        },
    };

    const axiosInstance = axios.create(defaultOptions);

    axiosInstance.interceptors.request.use(async function (config) {
        if (config.headers.Authorization) {
            return config;
        }
        const token = tokenRef.current || localStorage.getItem(TOKEN_KEY);
        const bearer = token ? `Bearer ${token}` : null;
        if (!bearer) {
            return config;
        }
        config.headers.Authorization = bearer;
        return config;
    });

    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config as (typeof error.config & { _retry?: boolean }) | undefined;
            const url = originalRequest?.url ?? "";
            const isAuthEndpoint =
                url.includes("/login") || url.includes("/refresh-token") || url.includes("/auth/azure/token");

            if (error.response?.status === 401 && !isAuthEndpoint && originalRequest && !originalRequest._retry) {
                originalRequest._retry = true;

                const newToken = await refreshAccessToken();
                if (newToken) {
                    originalRequest.headers = originalRequest.headers ?? {};
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return axiosInstance(originalRequest);
                }

                clearSession();
                window.location.href = "/login";
            }

            return Promise.reject(error);
        }
    );

    return axiosInstance;
};

export const axiosInstance = axiosClient();
